// ============================================================================
// CHANGO V5.0 — BOM Item Detail (Task #119 / Round 12)
// ============================================================================
// Single item detail page with BOM editor.
// Mount: /app/factory/bom-items/:code
//
// Sections:
//   1. Item basic info (editable)
//   2. BOM editor: manage child items + quantities
//   3. Parent references: read-only list of parents using this item
//
// boss/finance only (sales denied)
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

export default function BomItemDetail() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const isSales = user?.role === 'sales';
  const decodedCode = decodeURIComponent(code || '');

  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [bomLines, setBomLines] = useState([]);
  const [parentLines, setParentLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  const [allItems, setAllItems] = useState([]);

  const [form, setForm] = useState({
    name: '',
    name_en: '',
    category_id: '',
    unit: '',
    cost_rmb: '',
    supplier: '',
    notes: ''
  });

  const [addChildForm, setAddChildForm] = useState({
    child_code: '',
    qty_per_parent: '1'
  });

  // Fetch categories
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/api/material-categories');
        setCategories(res?.items || []);
      } catch {}
    };
    fetch();
  }, []);

  // Fetch item + BOM lines
  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const [itemRes, linesRes, parentRes, itemsRes] = await Promise.all([
          api.get(`/api/bom-items/${decodedCode}`),
          api.get(`/api/bom-lines?parent_code=${encodeURIComponent(decodedCode)}`),
          api.get(`/api/bom-lines?child_code=${encodeURIComponent(decodedCode)}`),
          api.get('/api/bom-items')
        ]);

        setItem(itemRes);
        setForm({
          name: itemRes.name || '',
          name_en: itemRes.name_en || '',
          category_id: itemRes.category_id || '',
          unit: itemRes.unit || '',
          cost_rmb: itemRes.cost_rmb ?? '',
          supplier: itemRes.supplier || '',
          notes: itemRes.notes || ''
        });

        setBomLines(linesRes?.items || []);
        setParentLines(parentRes?.items || []);
        setAllItems(itemsRes?.items || []);
      } catch (err) {
        setError(err.message || 'Failed to load item');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [decodedCode]);

  if (isSales) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          权限不足：仅 boss/finance 可访问工厂管理页面
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="px-6 py-12 text-center text-slate-400">Loading...</div>;
  }

  if (!item) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Item not found
        </div>
        <div className="mt-4">
          <Link to="/app/factory/bom-items" className="text-sm text-slate-600 hover:underline">
            Back to Items
          </Link>
        </div>
      </div>
    );
  }

  const handleSaveItem = async () => {
    try {
      setError(null);
      const payload = {
        name: form.name.trim(),
        name_en: form.name_en.trim() || null,
        category_id: form.category_id ? Number(form.category_id) : null,
        unit: form.unit.trim() || null,
        cost_rmb: form.cost_rmb ? Number(form.cost_rmb) : null,
        supplier: form.supplier.trim() || null,
        notes: form.notes.trim() || null
      };
      await api.patch(`/api/bom-items/${item.id}`, payload);
      const fresh = await api.get(`/api/bom-items/${decodedCode}`);
      setItem(fresh);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
  };

  const handleAddChild = async () => {
    if (!addChildForm.child_code.trim()) {
      setError('Select a child item');
      return;
    }

    try {
      setError(null);
      await api.post('/api/bom-lines', {
        parent_code: decodedCode,
        child_code: addChildForm.child_code.trim(),
        qty_per_parent: Number(addChildForm.qty_per_parent) || 1
      });
      // Refresh BOM lines
      const res = await api.get(`/api/bom-lines?parent_code=${encodeURIComponent(decodedCode)}`);
      setBomLines(res?.items || []);
      setShowAddChild(false);
      setAddChildForm({ child_code: '', qty_per_parent: '1' });
    } catch (err) {
      if (err.message && err.message.includes('CIRCULAR_BOM')) {
        setError('Cannot add: would create circular BOM reference');
      } else {
        setError(err.message || 'Failed to add child');
      }
    }
  };

  const handleDeleteBomLine = async (lineId) => {
    if (!window.confirm('Remove this BOM line?')) return;

    try {
      setError(null);
      await api.delete(`/api/bom-lines/${lineId}`);
      setBomLines(bomLines.filter((l) => l.id !== lineId));
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">{item.name}</h1>
          <p className="text-sm text-slate-500">Code: {item.code}</p>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      {/* ---- Item Details ---- */}
      <div className="card mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">基本信息</h2>
          {!editing && (
            <button onClick={() => setEditing(true)} className="btn-secondary text-sm">
              Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3 max-w-md">
            <label className="text-xs block">
              Name *
              <input
                type="text"
                className="field mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                maxLength="200"
              />
            </label>

            <label className="text-xs block">
              Name (EN)
              <input
                type="text"
                className="field mt-1"
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                maxLength="200"
              />
            </label>

            <label className="text-xs block">
              Category
              <select
                className="field mt-1"
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>

            <label className="text-xs block">
              Unit
              <input
                type="text"
                className="field mt-1"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                maxLength="20"
              />
            </label>

            <label className="text-xs block">
              Cost (RMB)
              <input
                type="number"
                className="field mt-1"
                value={form.cost_rmb}
                onChange={(e) => setForm({ ...form, cost_rmb: e.target.value })}
                step="0.01"
                min="0"
              />
            </label>

            <label className="text-xs block">
              Supplier
              <input
                type="text"
                className="field mt-1"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                maxLength="100"
              />
            </label>

            <label className="text-xs block">
              Notes
              <textarea
                className="field mt-1"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows="2"
                maxLength="500"
              />
            </label>

            <div className="flex gap-2 pt-4">
              <button onClick={handleSaveItem} className="btn-primary text-sm">
                Save
              </button>
              <button onClick={() => setEditing(false)} className="btn-ghost text-sm">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Name:</span> {form.name}
            </div>
            <div>
              <span className="text-slate-500">Name (EN):</span> {form.name_en || '—'}
            </div>
            <div>
              <span className="text-slate-500">Category:</span>{' '}
              {categories.find((c) => c.id === form.category_id)?.name || '—'}
            </div>
            <div>
              <span className="text-slate-500">Unit:</span> {form.unit || '—'}
            </div>
            <div>
              <span className="text-slate-500">Cost:</span>{' '}
              {form.cost_rmb ? `¥${Number(form.cost_rmb).toFixed(2)}` : '—'}
            </div>
            <div>
              <span className="text-slate-500">Supplier:</span> {form.supplier || '—'}
            </div>
            {form.notes && (
              <div className="col-span-2">
                <span className="text-slate-500">Notes:</span> {form.notes}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---- BOM Editor ---- */}
      <div className="card mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">BOM 编辑</h2>
          <button onClick={() => setShowAddChild(true)} className="btn-secondary text-sm">
            + 添加子项
          </button>
        </div>

        {bomLines.length === 0 ? (
          <p className="text-slate-500 text-sm">No child items</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Child Code</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Child Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Qty / Parent</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {bomLines.map((line) => (
                <tr key={line.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{line.child_code}</td>
                  <td className="px-4 py-2">
                    {allItems.find((it) => it.code === line.child_code)?.name || '—'}
                  </td>
                  <td className="px-4 py-2">{line.qty_per_parent}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleDeleteBomLine(line.id)}
                      className="text-xs text-rose-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- Parent References ---- */}
      <div className="card mb-6 p-6">
        <h2 className="text-lg font-semibold mb-4">作为子项被引用</h2>
        {parentLines.length === 0 ? (
          <p className="text-slate-500 text-sm">Not used as a child in any BOM</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Parent Code</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Parent Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Qty / Parent</th>
              </tr>
            </thead>
            <tbody>
              {parentLines.map((line) => (
                <tr key={line.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link
                      to={`/app/factory/bom-items/${encodeURIComponent(line.parent_code)}`}
                      className="text-chango-700 hover:underline"
                    >
                      {line.parent_code}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    {allItems.find((it) => it.code === line.parent_code)?.name || '—'}
                  </td>
                  <td className="px-4 py-2">{line.qty_per_parent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4">
        <Link to="/app/factory/bom-items" className="text-sm text-slate-600 hover:underline">
          Back to Items
        </Link>
      </div>

      {/* ---- Add Child Modal ---- */}
      {showAddChild && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Add Child Item</h2>

            <div className="space-y-3">
              <label className="text-xs block">
                Child Item *
                <select
                  className="field mt-1"
                  value={addChildForm.child_code}
                  onChange={(e) => setAddChildForm({ ...addChildForm, child_code: e.target.value })}
                >
                  <option value="">— Select —</option>
                  {allItems
                    .filter((it) => it.code !== decodedCode && !bomLines.some((bl) => bl.child_code === it.code))
                    .map((it) => (
                      <option key={it.id} value={it.code}>
                        {it.code} - {it.name}
                      </option>
                    ))}
                </select>
              </label>

              <label className="text-xs block">
                Quantity per Parent
                <input
                  type="number"
                  className="field mt-1"
                  value={addChildForm.qty_per_parent}
                  onChange={(e) => setAddChildForm({ ...addChildForm, qty_per_parent: e.target.value })}
                  placeholder="1"
                  step="0.0001"
                  min="0"
                />
              </label>

              <div className="flex gap-2 pt-4">
                <button onClick={handleAddChild} className="btn-primary text-sm flex-1">
                  Add
                </button>
                <button
                  onClick={() => setShowAddChild(false)}
                  className="btn-ghost text-sm flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
